import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
    MessageSquare, Plus, Search, Loader2, Clock, AlertTriangle,
    Plane, PlaneTakeoff, PlaneLanding, Wrench, CloudRain, Radio,
    CheckCheck, Megaphone, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
    delay: { label: 'Demora', icon: Clock, color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
    unscheduled_maintenance: { label: 'Mant. No Programado', icon: Wrench, color: 'bg-red-500/10 text-red-500 border-red-500/20' },
    departure: { label: 'Salida', icon: PlaneTakeoff, color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    arrival: { label: 'Llegada', icon: PlaneLanding, color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
    general: { label: 'Aviso General', icon: Megaphone, color: 'bg-primary/10 text-primary border-primary/20' },
    irops: { label: 'IROPS', icon: AlertTriangle, color: 'bg-red-500/10 text-red-500 border-red-500/20' },
    weather: { label: 'Meteorologia', icon: CloudRain, color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20' },
};

const priorityConfig = {
    urgent: { label: 'Urgente', color: 'bg-red-500 text-white' },
    high: { label: 'Alto', color: 'bg-amber-500 text-white' },
    normal: { label: 'Normal', color: 'bg-muted text-muted-foreground' },
};

const roleLabels = {
    admin: 'Admin', dispatcher: 'Despacho', ccod: 'CCO', maintenance: 'Mantenimiento',
    pilot: 'Tripulacion', counter: 'Mostrador', ramp: 'Rampa', comercial: 'Comercial',
    calidad: 'Calidad', gerencia: 'Dir. Ejecutiva'
};

export default function OpMessagesPage() {
    const { user, hasRole } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newMsg, setNewMsg] = useState({
        message_type: 'general', priority: 'normal', title: '', body: '',
        flight_number: '', aircraft_registration: ''
    });

    const token = localStorage.getItem('aeroops_token');
    const headers = { Authorization: `Bearer ${token}` };
    const canCreate = hasRole('admin') || hasRole('ccod') || hasRole('dispatcher') || hasRole('maintenance');
    const canDelete = hasRole('admin') || hasRole('ccod');

    const fetchMessages = useCallback(async () => {
        setLoading(true);
        try {
            const params = typeFilter !== 'all' ? `?msg_type=${typeFilter}` : '';
            const res = await axios.get(`${API}/op-messages${params}`, { headers });
            setMessages(res.data);
        } catch {
            toast.error('Error al cargar mensajes');
        } finally {
            setLoading(false);
        }
    }, [typeFilter]);

    useEffect(() => { fetchMessages(); }, [fetchMessages]);

    const createMessage = async () => {
        if (!newMsg.title.trim()) { toast.error('Titulo requerido'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/op-messages`, newMsg, { headers });
            toast.success('Mensaje enviado a todas las areas');
            setShowCreate(false);
            setNewMsg({ message_type: 'general', priority: 'normal', title: '', body: '', flight_number: '', aircraft_registration: '' });
            fetchMessages();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar');
        } finally {
            setSaving(false);
        }
    };

    const acknowledgeMsg = async (msgId) => {
        try {
            await axios.patch(`${API}/op-messages/${msgId}/acknowledge`, {}, { headers });
            toast.success('Mensaje acusado');
            fetchMessages();
        } catch {
            toast.error('Error');
        }
    };

    const deleteMsg = async (msgId) => {
        try {
            await axios.delete(`${API}/op-messages/${msgId}`, { headers });
            toast.success('Mensaje eliminado');
            fetchMessages();
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const filtered = messages.filter(m => {
        if (!search) return true;
        const s = search.toLowerCase();
        return m.title?.toLowerCase().includes(s) || m.body?.toLowerCase().includes(s) || m.flight_number?.toLowerCase().includes(s);
    });

    const unreadCount = messages.filter(m => !m.read_by?.includes(user?.id)).length;

    const timeSince = (isoStr) => {
        const diff = Date.now() - new Date(isoStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'ahora';
        if (mins < 60) return `${mins}m`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h`;
        return `${Math.floor(hrs / 24)}d`;
    };

    return (
        <div className="space-y-6" data-testid="op-messages-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Radio className="w-6 h-6 text-primary" />Mensajes Operacionales
                    </h1>
                    <span className="text-sm text-muted-foreground mt-1">
                        Avisos broadcast para todas las areas
                        {unreadCount > 0 && <Badge className="ml-2 bg-primary text-white">{unreadCount} sin leer</Badge>}
                    </span>
                </div>
                {canCreate && (
                    <Button onClick={() => setShowCreate(true)} data-testid="create-op-msg-btn">
                        <Plus className="w-4 h-4 mr-1" />Nuevo Mensaje
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Buscar mensajes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="op-msg-search" />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[200px]" data-testid="op-msg-type-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los tipos</SelectItem>
                        <SelectItem value="delay">Demoras</SelectItem>
                        <SelectItem value="departure">Salidas</SelectItem>
                        <SelectItem value="arrival">Llegadas</SelectItem>
                        <SelectItem value="unscheduled_maintenance">Mant. No Programado</SelectItem>
                        <SelectItem value="irops">IROPS</SelectItem>
                        <SelectItem value="weather">Meteorologia</SelectItem>
                        <SelectItem value="general">Avisos Generales</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Messages Feed */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : filtered.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center text-muted-foreground">
                        <Radio className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No hay mensajes operacionales</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2" data-testid="op-messages-list">
                    {filtered.map(msg => {
                        const tc = typeConfig[msg.message_type] || typeConfig.general;
                        const pc = priorityConfig[msg.priority] || priorityConfig.normal;
                        const TypeIcon = tc.icon;
                        const isRead = msg.read_by?.includes(user?.id);
                        const isAcked = msg.acknowledged_by?.some(a => a.user_id === user?.id);
                        const ackCount = msg.acknowledged_by?.length || 0;
                        return (
                            <Card
                                key={msg.id}
                                className={`transition-all ${!isRead ? 'border-l-2 border-l-primary bg-primary/[0.02]' : 'opacity-80'}`}
                                data-testid={`op-msg-${msg.id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tc.color}`}>
                                            <TypeIcon className="w-4.5 h-4.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <Badge className={tc.color + ' text-[10px]'}>{tc.label}</Badge>
                                                {msg.priority !== 'normal' && <Badge className={pc.color + ' text-[10px]'}>{pc.label}</Badge>}
                                                {msg.flight_number && <span className="font-mono text-xs font-bold">{msg.flight_number}</span>}
                                                {msg.aircraft_registration && <span className="font-mono text-[10px] text-muted-foreground">{msg.aircraft_registration}</span>}
                                            </div>
                                            <p className="font-medium text-sm">{msg.title}</p>
                                            {msg.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{msg.body}</p>}
                                            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                                                <span>{roleLabels[msg.sender_role] || msg.sender_role}: {msg.sender_name}</span>
                                                <span>{timeSince(msg.created_at)}</span>
                                                {ackCount > 0 && <span className="flex items-center gap-0.5"><CheckCheck className="w-3 h-3 text-emerald-500" />{ackCount} acusaron</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {!isAcked && (
                                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => acknowledgeMsg(msg.id)} data-testid={`ack-msg-${msg.id}`}>
                                                    <CheckCheck className="w-3.5 h-3.5 mr-1" />Acusar
                                                </Button>
                                            )}
                                            {isAcked && <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/30">Acusado</Badge>}
                                            {canDelete && (
                                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => deleteMsg(msg.id)} data-testid={`del-msg-${msg.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Create Message Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[550px]" data-testid="create-op-msg-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Radio className="w-5 h-5 text-primary" />Nuevo Mensaje Operacional</DialogTitle>
                        <DialogDescription>Este mensaje sera visible para todas las areas de la aerolinea</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Tipo</Label>
                                <Select value={newMsg.message_type} onValueChange={v => setNewMsg({...newMsg, message_type: v})}>
                                    <SelectTrigger data-testid="op-msg-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="delay">Demora</SelectItem>
                                        <SelectItem value="departure">Salida de Vuelo</SelectItem>
                                        <SelectItem value="arrival">Llegada de Vuelo</SelectItem>
                                        <SelectItem value="unscheduled_maintenance">Mant. No Programado</SelectItem>
                                        <SelectItem value="irops">IROPS</SelectItem>
                                        <SelectItem value="weather">Meteorologia</SelectItem>
                                        <SelectItem value="general">Aviso General</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Prioridad</Label>
                                <Select value={newMsg.priority} onValueChange={v => setNewMsg({...newMsg, priority: v})}>
                                    <SelectTrigger data-testid="op-msg-priority-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="high">Alto</SelectItem>
                                        <SelectItem value="urgent">Urgente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Titulo *</Label>
                            <Input value={newMsg.title} onChange={e => setNewMsg({...newMsg, title: e.target.value})} placeholder="Ej: AD-101 demorado 30 min por clima" data-testid="op-msg-title" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Detalle</Label>
                            <Textarea value={newMsg.body} onChange={e => setNewMsg({...newMsg, body: e.target.value})} rows={3} placeholder="Informacion adicional..." data-testid="op-msg-body" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Vuelo (opcional)</Label>
                                <Input value={newMsg.flight_number} onChange={e => setNewMsg({...newMsg, flight_number: e.target.value.toUpperCase()})} placeholder="AD-101" data-testid="op-msg-flight" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Matricula (opcional)</Label>
                                <Input value={newMsg.aircraft_registration} onChange={e => setNewMsg({...newMsg, aircraft_registration: e.target.value.toUpperCase()})} placeholder="HK-5100" data-testid="op-msg-aircraft" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createMessage} disabled={saving} data-testid="send-op-msg-btn">
                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enviar a Todas las Areas
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
